import { useEffect, useState } from "react";
import { api, brl, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { PackagePlus, Trash2, Pencil, Loader2, ChevronDown, Check, X, MessageCircle } from "lucide-react";

const empty = { id: null, name: "", description: "", price: "", limit: "", slug: "", active: true };
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("pt-BR") : "");

function AddonForm({ open, onClose, initial, onSaved }) {
  const [f, setF] = useState(initial || empty);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setF(initial || empty); }, [initial]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target?.value ?? e }));
  const submit = async () => {
    setSaving(true);
    try {
      const payload = { ...f, price: Number(f.price || 0), limit: f.limit ? Number(f.limit) : null };
      const { data } = f.id ? await api.patch(`/admin/addons/${f.id}`, payload) : await api.post("/admin/addons", payload);
      toast.success(f.id ? "Pacote atualizado" : "Pacote criado");
      onSaved(data); onClose();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="addon-form" className="bg-zinc-950 border border-zinc-800 text-zinc-100 max-w-md">
        <DialogHeader><DialogTitle className="font-display uppercase">{f.id ? "Editar pacote" : "Novo pacote"}</DialogTitle>
          <DialogDescription className="text-zinc-400 text-xs">Slug opcional (ex.: <code>plate_lookup</code>) liga o pacote a um recurso existente.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Input data-testid="addon-name" placeholder="Nome" value={f.name} onChange={set("name")} className="h-10 bg-zinc-900 border-zinc-800" />
          <Textarea data-testid="addon-desc" placeholder="Descrição" value={f.description} onChange={set("description")} className="min-h-[80px] bg-zinc-900 border-zinc-800" />
          <div className="grid grid-cols-3 gap-2">
            <Input data-testid="addon-price" placeholder="Preço R$" inputMode="decimal" value={f.price} onChange={set("price")} className="h-10 bg-zinc-900 border-zinc-800" />
            <Input data-testid="addon-limit" placeholder="Limite/mês" inputMode="numeric" value={f.limit} onChange={set("limit")} className="h-10 bg-zinc-900 border-zinc-800" />
            <Input data-testid="addon-slug" placeholder="Slug (opcional)" value={f.slug} onChange={set("slug")} className="h-10 bg-zinc-900 border-zinc-800" />
          </div>
          <label className="flex items-center justify-between gap-2 rounded-lg border border-zinc-800 px-3 py-2 text-sm">
            Pacote ativo (visível para oficinas) <Switch data-testid="addon-active" checked={!!f.active} onCheckedChange={set("active")} />
          </label>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="bg-zinc-900 border-zinc-800">Cancelar</Button>
          <Button data-testid="addon-save" onClick={submit} disabled={saving} className="bg-green-brand hover:bg-green-brandDark text-white font-bold">{saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Salvar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const waUrl = (phone, text) => {
  const d = (phone || "").replace(/\D/g, "");
  if (!d) return null;
  return `https://wa.me/${d.length >= 12 ? d : `55${d}`}?text=${encodeURIComponent(text)}`;
};

export function AddonsAdmin() {
  const [open, setOpen] = useState(false);
  const [addons, setAddons] = useState([]);
  const [requests, setRequests] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = async () => {
    try {
      const [a, r] = await Promise.all([api.get("/admin/addons"), api.get("/admin/addon-requests")]);
      setAddons(a.data); setRequests(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);
  const del = async (a) => { if (!window.confirm(`Excluir pacote "${a.name}"?`)) return; try { await api.delete(`/admin/addons/${a.id}`); setAddons((p) => p.filter((x) => x.id !== a.id)); } catch (e) { toast.error(formatApiError(e)); } };
  const approve = async (r) => { try { await api.post(`/admin/addon-requests/${r.id}/approve`); toast.success("Pacote ativado"); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const reject = async (r) => { try { await api.delete(`/admin/addon-requests/${r.id}`); toast.success("Pedido recusado"); load(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <section data-testid="addons-admin" className="space-y-3">
      <button type="button" data-testid="addons-admin-toggle" onClick={() => setOpen((o) => !o)}
              className="w-full flex items-center justify-between gap-2 rounded-xl border border-zinc-800 bg-zinc-950/60 p-3 hover:bg-zinc-900">
        <span className="inline-flex items-center gap-2 font-display text-lg font-black uppercase text-zinc-100"><PackagePlus className="h-5 w-5 text-sky-300" /> Pacotes adicionais <span className="text-xs text-zinc-400">({requests.length} pedido(s))</span></span>
        <ChevronDown className={`h-5 w-5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="space-y-4">
          {requests.length > 0 && (
            <div data-testid="addon-requests" className="rounded-xl border border-sky-800 bg-sky-950/20 p-3">
              <div className="font-mono-caps text-xs text-sky-200 uppercase mb-2">Pedidos pendentes</div>
              <ul className="space-y-1.5">
                {requests.map((rq) => {
                  const wa = waUrl(rq.owner_phone, `Olá ${rq.owner_name || ""}, estou ativando o pacote ${rq.addon_name} na sua oficina.`);
                  return (
                    <li key={rq.id} data-testid={`addon-req-${rq.id}`} className="flex items-center justify-between gap-2 text-sm">
                      <div className="min-w-0"><strong>{rq.shop_name}</strong> → {rq.addon_name} ({brl(rq.price)}/mês) <span className="text-[11px] text-zinc-500 block">{fmt(rq.requested_at)}</span></div>
                      <div className="flex items-center gap-1 shrink-0">
                        {wa && <a href={wa} target="_blank" rel="noopener noreferrer" className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-emerald-700 hover:bg-emerald-600 text-white"><MessageCircle className="h-4 w-4" /></a>}
                        <Button data-testid={`addon-req-approve-${rq.id}`} onClick={() => approve(rq)} className="h-8 bg-green-brand hover:bg-green-brandDark text-white"><Check className="h-4 w-4 mr-1" /> Aprovar</Button>
                        <Button data-testid={`addon-req-reject-${rq.id}`} onClick={() => reject(rq)} variant="outline" className="h-8 bg-zinc-900 border-zinc-800 text-red-300"><X className="h-4 w-4" /></Button>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          <div className="flex items-center justify-between"><div className="text-xs text-zinc-400">Catálogo de pacotes</div>
            <Button data-testid="addon-new" onClick={() => setEdit({})} className="h-9 bg-green-brand hover:bg-green-brandDark text-white font-bold">Novo pacote</Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {addons.map((a) => (
              <div key={a.id} data-testid={`admin-addon-${a.id}`} className="rounded-xl border border-zinc-800 bg-zinc-950/60 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-bold text-zinc-100">{a.name} {a.slug && <code className="text-[10px] text-zinc-500">({a.slug})</code>}</div>
                    <p className="text-xs text-zinc-400">{a.description || "Sem descrição."}</p>
                    <div className="text-[11px] text-zinc-500 mt-1">{brl(a.price)}/mês {a.limit ? `· limite ${a.limit}` : ""} · {a.active ? "ativo" : "inativo"}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => setEdit(a)} className="h-8 w-8 text-zinc-300 hover:text-zinc-100 hover:bg-zinc-800"><Pencil className="h-3.5 w-3.5" /></Button>
                    <Button variant="ghost" size="icon" onClick={() => del(a)} className="h-8 w-8 text-red-400 hover:bg-red-950/40"><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <AddonForm open={!!edit} onClose={() => setEdit(null)} initial={edit?.id ? edit : empty} onSaved={() => load()} />
        </div>
      )}
    </section>
  );
}
